import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldX, Loader2 } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { Page, Card } from "../components/UI";

const rank = (v) => (v ? `#${Number(v).toLocaleString("en-US")}` : "—");
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).toUpperCase();

function Field({ label, value, testid, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono">{label}</div>
      <div className={`mt-1 text-slate-100 ${mono ? "font-mono text-[#E5B842] text-xl" : "text-lg"}`} data-testid={testid}>{value}</div>
    </div>
  );
}

export default function CertificateVerify() {
  const { certNo } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/certificates/${encodeURIComponent(certNo)}/verify`).then(({ data }) => setC(data))
      .catch((e) => setErr(e?.response?.status === 404 ? "No certificate with this number exists." : apiErr(e, "Verification failed")));
  }, [certNo]);

  const valid = c?.status === "VALID";
  return (
    <Page>
      <div className="max-w-2xl mx-auto py-6" data-testid="certificate-verify-page">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-2">WSTF · Certificate verification</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-slate-50 leading-none break-all">{certNo}</h1>
        <Card className="p-6 mt-8">
          {err ? (
            <div className="flex items-center gap-3 text-rose-300" data-testid="cert-verify-not-found"><ShieldX className="w-6 h-6" /> {err}</div>
          ) : !c ? (
            <Loader2 className="w-5 h-5 animate-spin text-[#E5B842]" />
          ) : (
            <>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border font-heading font-black uppercase tracking-wide ${valid ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-rose-500/40 bg-rose-500/10 text-rose-300"}`} data-testid="cert-verify-status">
                {valid ? <ShieldCheck className="w-5 h-5" /> : <ShieldX className="w-5 h-5" />} {c.status}
              </div>
              <p className="text-sm text-slate-400 mt-3">
                {valid ? "This certificate was issued by the World Sports Trading Federation and is genuine. Ranks shown are as of the issue date."
                  : `This certificate was revoked on ${fmtDate(c.revoked_at)} and is no longer valid.`}
              </p>
              {!valid && c.revoke_reason && (
                <p className="text-sm text-rose-200/80 mt-2" data-testid="cert-verify-revoke-reason">Reason: {c.revoke_reason}</p>
              )}
              <div className="mt-6 font-serif italic text-3xl text-[#E5B842] break-words" data-testid="cert-verify-name">{c.member_name}</div>
              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                <Field label="World rank (at issue)" value={rank(c.world_rank)} testid="cert-verify-world-rank" mono />
                <Field label={`${(c.country_name || "Country").toUpperCase()} rank (at issue)`} value={rank(c.country_rank)} testid="cert-verify-country-rank" mono />
                <Field label="Country" value={c.country_name || "—"} testid="cert-verify-country" />
                <Field label="Issued" value={fmtDate(c.issued_at)} testid="cert-verify-issued" />
              </div>
            </>
          )}
        </Card>
        <Link to="/" className="inline-block mt-6 text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-[#E5B842]">← WSTF home</Link>
      </div>
    </Page>
  );
}
